'use client';

import { useState } from 'react';

import { Button } from '@flipster/react/design-system/button';
import { RadioButton } from '@flipster/react/design-system/radio-button';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import {
  getEarnSunsetPhase,
  persistEarnSunsetPhaseOverride,
  readEarnSunsetPhaseOverride,
  type EarnSunsetPhase,
} from '@flipster/utils/earn-sunset';

type PhaseChoice = EarnSunsetPhase | 'default';

const choices: { value: PhaseChoice; label: string; hint: string }[] = [
  { value: 'default', label: 'Follow dates', hint: 'UTC constants in this build' },
  { value: 'pre-buffer', label: 'pre-buffer', hint: 'subscribe still open' },
  { value: 'buffer', label: 'buffer', hint: 'redeem-only + sunset modal' },
  { value: 'hidden', label: 'hidden', hint: 'PUSDT card/row hidden; /earn stays' },
];

export const EarnSunsetPhaseOverride = () => {
  const [choice, setChoice] = useState<PhaseChoice>(() => readEarnSunsetPhaseOverride() ?? 'default');
  const [dirty, setDirty] = useState(false);
  const clockPhase = getEarnSunsetPhase(Date.now(), null);

  const apply = () => {
    persistEarnSunsetPhaseOverride(choice === 'default' ? null : choice);
    location.reload();
  };

  return (
    <div className={stack({ align: 'stretch', gap: 8, flexShrink: 0 })}>
      <h2 className={hstack({ gap: 16, textStyle: 'Title/XS' })}>
        <span>Earn sunset phase</span>
        <Button
          hierarchy='primary'
          color='accent'
          size='small'
          label='Apply & Reload'
          disabled={!dirty}
          onClick={apply}
        />
      </h2>
      <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>
        Clock is {clockPhase}. Override is session-only and ignored on prod hosts.
      </p>
      <ul className={stack({ gap: 4, p: 8, rounded: 4, bgColor: 'background.default.level-0' })}>
        {choices.map(({ value, label, hint }) => (
          <li key={value}>
            <RadioButton
              name='earn-sunset-phase'
              checked={choice === value}
              onChange={() => {
                setDirty(true);
                setChoice(value);
              }}
            >
              {label}{' '}
              <span className={css({ textStyle: 'Text/XS/Regular', color: 'content.default.level-3' })}>({hint})</span>
            </RadioButton>
          </li>
        ))}
      </ul>
    </div>
  );
};
